import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setAccessToken } from '@/auth/tokenStore';
import { persistActiveTenantId } from '@/lib/tenant';
import { signinRequest, signoutRequest, signupRequest, type AuthUser } from '@/lib/api/auth';

const TOKEN_KEY = 'saasd.accessToken';

interface LocalAuthValue {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, workspaceName: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const LocalAuthContext = createContext<LocalAuthValue | undefined>(undefined);

function readToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Local credential auth state. Holds the backend-issued access token (persisted
 * to localStorage so a refresh keeps the session) and keeps the non-React API
 * client token store in sync. Mounted only in local auth mode.
 */
export function LocalAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readToken());
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    setAccessToken(token);
  }, [token]);

  const persist = useCallback((next: string | null) => {
    setToken(next);
    if (typeof localStorage === 'undefined') return;
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const res = await signinRequest({ email, password });
      setUser(res.user);
      persist(res.access_token);
    },
    [persist],
  );

  const signUp = useCallback(
    async (email: string, password: string, workspaceName: string) => {
      const res = await signupRequest({ email, password, workspace_name: workspaceName });
      setUser(res.user);
      persist(res.access_token);
      // The freshly created workspace becomes the active one immediately.
      persistActiveTenantId(res.tenant.id);
    },
    [persist],
  );

  const signOut = useCallback(async () => {
    try {
      if (token) await signoutRequest();
    } catch {
      // Best-effort: the token is stateless, so clear locally regardless.
    }
    setUser(null);
    persist(null);
    persistActiveTenantId(null);
  }, [persist, token]);

  const value = useMemo<LocalAuthValue>(
    () => ({ token, user, isAuthenticated: Boolean(token), signIn, signUp, signOut }),
    [token, user, signIn, signUp, signOut],
  );

  return <LocalAuthContext.Provider value={value}>{children}</LocalAuthContext.Provider>;
}

export function useLocalAuthContext(): LocalAuthValue {
  const ctx = useContext(LocalAuthContext);
  if (!ctx) {
    throw new Error('useLocalAuthContext must be used within a LocalAuthProvider');
  }
  return ctx;
}
